import _ from 'lodash';
import { confirmLexicalStateInTextComponent } from 'modules/draftjs2lexical';
import * as lexical from 'modules/Lexical/helpers';
import * as lexicalRelationStyles from 'modules/Lexical/TextRelationStyles';
import * as Models from 'models';
import { isCallToAction, isReusableLayoutDocument, isTextComponent } from 'utils/entityType';
import { toImmutable } from 'utils/immutable';
import { isRegularRelation } from 'utils/relations/isRegularRelation';

type Args = {
  layoutDocument: Models.ReusableLayout;
  projectBrandDefinition: Models.OnlineBrandDefinition;
  documentsToSet?: Models.CombinedDocuments;
};

// NOTE: have to adapt styles only when adding(dropping) new RL/GRL to artboard
// adaptStyles flag should be set to false for other operations, e.g. refresh, upload etc
export function applyStyles<T extends Args>(
  args: T,
  adaptStyles: boolean,
): T {
  const {
    layoutDocument,
    projectBrandDefinition,
    documentsToSet: originalDocumentsToSet = {},
  } = args;

  const documentsToSet: Models.CombinedDocuments = { ...originalDocumentsToSet };

  const {
    documents,
    entities: {
      brandDefinition: extendedBrandDefinition,
      layoutSourceBrandDefinition,
      relations,
    },
  } = layoutDocument;

  // no layout source brand definition is absent in case we unlinked layout
  // TODO: add layoutSourceBrandDefinition while unlinking
  const safeLayoutSourceBrandDefinition = layoutSourceBrandDefinition || extendedBrandDefinition;
  const projectBrandStyles = toImmutable(projectBrandDefinition.brandStyles);
  const layoutBrandstyles = toImmutable(safeLayoutSourceBrandDefinition.brandStyles);
  const brandStyles = toImmutable(extendedBrandDefinition.brandStyles);
  const colors = toImmutable(extendedBrandDefinition.colors);
  const fonts = toImmutable(extendedBrandDefinition.fonts);
  const layoutColors = toImmutable(safeLayoutSourceBrandDefinition.colors);
  const layoutFonts = toImmutable(safeLayoutSourceBrandDefinition.fonts);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  _.forEach(relations, (relation) => {
    if (!isRegularRelation(relation)) {
      return;
    }
    const { documentId } = relation;
    if (!documentId) {
      return;
    }
    if (isCallToAction(relation)) {
      const callToActionComponent = (documentsToSet[documentId] ?? documents[documentId]) as Models.CallToAction | undefined;
      if (!callToActionComponent) {
        return;
      }
      documentsToSet[documentId] = lexical.applyProjectFontStyleToCTAComponent({
        callToActionComponent,
        colors,
        fonts,
      });

      return;
    }

    if (!isTextComponent(relation)) {
      return;
    }

    const { styles } = relation;
    const { brandStyleId, brandStyleChanged } = styles;

    if (!brandStyleId) {
      return;
    }

    let textComponent = (documentsToSet[documentId] ?? documents[documentId]) as Models.TextComponent | undefined;
    if (textComponent) {
      textComponent = confirmLexicalStateInTextComponent(textComponent, styles, layoutFonts);
    }

    const projectBrandStyle = projectBrandStyles.get(brandStyleId);
    const isForcedTargetProjectBrandStyle = projectBrandStyle?.get('forced', false) && isReusableLayoutDocument(layoutDocument);

    if (brandStyleChanged && !isForcedTargetProjectBrandStyle) {
      const layoutBrandStyle = layoutBrandstyles.get(brandStyleId);

      if (!projectBrandStyle || !adaptStyles) {
        return;
      }
      const layoutBrandProps = { brandStyle: layoutBrandStyle, colors: layoutColors, fonts: layoutFonts };
      const projectBrandProps = { brandStyle: projectBrandStyle, colors, fonts };

      relation.styles = lexicalRelationStyles.mergeBrandStylesOnRelation(styles, layoutBrandProps, projectBrandProps);
      if (textComponent) {
        documentsToSet[documentId] = lexical.mergeBrandStylesOnComponent(
          textComponent,
          layoutBrandProps,
          projectBrandProps,
        );
      }

      return;
    }

    const brandStyle = brandStyles.get(brandStyleId);
    relation.styles = lexicalRelationStyles.applyBrandStyleOnRelation(
      relation.styles,
      { brandStyle, colors, fonts },
    );
    if (textComponent) {
      documentsToSet[documentId] = lexical.applyBrandStyleOnComponent(
        textComponent,
        brandStyle,
        colors,
        fonts,
      );
    }
  });

  return { ...args, documentsToSet };
}
